/**
 * Final Payment Preparation — hybrid checklist + planner vs executor flows.
 */

import { useCallback, useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { ChevronLeft, Pencil, Trash2 } from 'lucide-react';
import { useSettlementSession } from '../../../hooks/useSettlementSession';
import { useSettlement } from '../../../contexts/SettlementContext';
import { persistPayDebtsHubSectionAndSyncDt3 } from './payDebtsHub';
import { readMaybeDataUrl } from './payDebtsFileUtils';

const FRAME_ID = 'sframe-204-s1';

const PREP_ITEMS: { id: string; title: string; description: string }[] = [
  {
    id: 'prep-1',
    title: 'Confirm Estate Account Balance',
    description: 'Verify the current balance in the estate bank account is sufficient to cover scheduled payments.',
  },
  {
    id: 'prep-2',
    title: 'Verify Creditor Payment Methods',
    description: "Confirm each creditor's preferred payment method: check, wire, portal, etc.",
  },
  {
    id: 'prep-3',
    title: 'Review Final "No-Pay" List',
    description: 'Confirm list of disputed or time-barred claims that will NOT be paid.',
  },
];

export type CreditorNotifyRow = {
  id: string;
  creditorName: string;
  balance: string;
  contactMethod: string;
  documents: string;
};

function extractPlannerCreditors(plannerBlob: Record<string, unknown> | null | undefined): CreditorNotifyRow[] {
  if (!plannerBlob) return [];
  const taskDebts =
    plannerBlob.taskProgress &&
    typeof plannerBlob.taskProgress === 'object' &&
    (plannerBlob.taskProgress as Record<string, unknown>).debts &&
    typeof (plannerBlob.taskProgress as Record<string, unknown>).debts === 'object'
      ? ((plannerBlob.taskProgress as Record<string, unknown>).debts as Record<string, unknown>)
      : undefined;
  const nested = taskDebts?.answers as Record<string, unknown> | undefined;
  const arr = nested?.debts;
  const raw =
    Array.isArray(arr)
      ? arr
      : Array.isArray((plannerBlob as { debts?: unknown[] }).debts)
        ? (plannerBlob as { debts: unknown[] }).debts
        : [];
  if (!Array.isArray(raw)) return [];
  const rows: CreditorNotifyRow[] = [];
  for (let i = 0; i < raw.length; i++) {
    const row = raw[i];
    if (!row || typeof row !== 'object') continue;
    const o = row as Record<string, unknown>;
    const name = typeof o.creditor === 'string' ? o.creditor : typeof o.name === 'string' ? o.name : '';
    if (!name.trim()) continue;
    const amt = o.amount != null ? String(o.amount) : '—';
    const docs = Array.isArray(o.documents) ? `${o.documents.length} file(s)` : '—';
    const contact = typeof o.notes === 'string' ? o.notes : typeof o.accountNumber === 'string' ? o.accountNumber : '—';
    rows.push({
      id: String(o.id ?? `planner-${i}`),
      creditorName: name,
      balance: amt,
      contactMethod: contact.slice(0, 200),
      documents: docs,
    });
  }
  return rows;
}

export default function PayDebts_FinalPrep() {
  const navigate = useNavigate();
  const { estateId } = useParams<{ estateId: string }>();
  const { isArchived } = useSettlement();
  const { loadFrameData, saveFrameData, loadPlannerData, updateStepProgress, error } = useSettlementSession(
    estateId ?? null,
    { readOnly: isArchived }
  );

  const [checks, setChecks] = useState<Record<string, boolean>>(() =>
    Object.fromEntries(PREP_ITEMS.map(({ id }) => [id, false]))
  );
  const [creditors, setCreditors] = useState<CreditorNotifyRow[]>([]);
  const [mailScanFiles, setMailScanFiles] = useState<{ name: string; dataUrl?: string }[]>([]);
  const [creditReports, setCreditReports] = useState<{ name: string; dataUrl?: string }[]>([]);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [draft, setDraft] = useState<Partial<CreditorNotifyRow> | null>(null);

  useEffect(() => {
    let cancelled = false;
    void (async () => {
      if (!estateId) return;
      const framed = await loadFrameData(FRAME_ID);
      const d = framed?.data as Record<string, unknown> | undefined;
      const c = (d?.checks as Record<string, boolean> | undefined) || {};
      setChecks(Object.fromEntries(PREP_ITEMS.map(({ id }) => [id, !!c[id]])));
      const savedRows = (d?.creditorNotifications as CreditorNotifyRow[] | undefined) || [];
      if (savedRows.length > 0) {
        setCreditors(savedRows);
      } else {
        const planner = (await loadPlannerData('frame-235')) as Record<string, unknown> | null;
        const seeded = extractPlannerCreditors(planner);
        if (seeded.length > 0 && !cancelled) setCreditors(seeded);
      }
      const m = (d?.mailScanFiles as { name: string; dataUrl?: string }[]) || [];
      const cr = (d?.creditReportFiles as { name: string; dataUrl?: string }[]) || [];
      if (!cancelled) {
        setMailScanFiles(m);
        setCreditReports(cr);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [estateId, loadFrameData, loadPlannerData]);

  const plannerPathUx = creditors.length > 0;

  const persistFull = useCallback(
    async (next: Partial<Record<string, unknown>>) => {
      if (!estateId || isArchived) return;
      const framed = await loadFrameData(FRAME_ID);
      const prev =
        typeof framed?.data === 'object' && framed?.data ? (framed.data as Record<string, unknown>) : {};
      const merged = { ...prev, ...next };
      await saveFrameData(FRAME_ID, merged);

      const nextChecks = (merged.checks as Record<string, boolean> | undefined) ?? {};
      const allPrep = PREP_ITEMS.every(({ id }) => nextChecks[id]);
      await persistPayDebtsHubSectionAndSyncDt3(
        loadFrameData,
        saveFrameData,
        updateStepProgress,
        's1',
        !!allPrep
      );
    },
    [estateId, isArchived, loadFrameData, saveFrameData, updateStepProgress]
  );

  const togglePrep = async (itemId: string) => {
    if (isArchived) return;
    const nextChecks = { ...checks, [itemId]: !checks[itemId] };
    setChecks(nextChecks);
    await persistFull({ checks: nextChecks });
  };

  const saveCreditors = async (next: CreditorNotifyRow[]) => {
    setCreditors(next);
    await persistFull({ creditorNotifications: next });
  };

  const startEdit = (row: CreditorNotifyRow) => {
    setEditingId(row.id);
    setDraft({ ...row });
  };

  const saveEditRow = async () => {
    if (!draft?.creditorName?.trim()) return;
    const next = creditors.map((r) =>
      r.id === editingId ? { ...r, ...draft } as CreditorNotifyRow : r
    );
    setEditingId(null);
    setDraft(null);
    await saveCreditors(next);
  };

  const deleteRow = async (id: string) => {
    await saveCreditors(creditors.filter((r) => r.id !== id));
  };

  const onDropMail = async (files: FileList | null) => {
    if (!files?.length || isArchived || !estateId) return;
    const additions: { name: string; dataUrl?: string }[] = [...mailScanFiles];
    for (const file of Array.from(files)) {
      additions.push(await readMaybeDataUrl(file, 420_000));
    }
    setMailScanFiles(additions);
    const framed = await loadFrameData(FRAME_ID);
    const prev =
      typeof framed?.data === 'object' && framed?.data ? (framed.data as Record<string, unknown>) : {};
    await saveFrameData(FRAME_ID, { ...prev, mailScanFiles: additions });
  };

  const onDropCreditReports = async (files: FileList | null) => {
    if (!files?.length || isArchived || !estateId) return;
    const additions: { name: string; dataUrl?: string }[] = [...creditReports];
    for (const file of Array.from(files)) {
      additions.push(await readMaybeDataUrl(file, 420_000));
    }
    setCreditReports(additions);
    const framed = await loadFrameData(FRAME_ID);
    const prev =
      typeof framed?.data === 'object' && framed?.data ? (framed.data as Record<string, unknown>) : {};
    await saveFrameData(FRAME_ID, { ...prev, creditReportFiles: additions });
  };

  if (!estateId) return <div className="p-8 text-stone-600 text-sm">Select an estate.</div>;

  return (
    <div className="max-w-2xl mx-auto py-8 px-4">
      {error ? (
        <div className="mb-4 bg-red-50 border border-red-200 rounded-lg p-3 text-red-700 text-sm">{error}</div>
      ) : null}
      <button
        type="button"
        onClick={() => navigate(`/sframe-204/${estateId}`)}
        className="flex items-center gap-2 text-stone-500 hover:text-stone-700 mb-6 text-sm"
      >
        <ChevronLeft className="w-4 h-4" /> Pay Validated Debts
      </button>
      <h1 className="text-2xl font-semibold text-stone-800 mb-6">Final Payment Preparation</h1>

      <div className="space-y-3 mb-10">
        {PREP_ITEMS.map((item) => (
          <div key={item.id} className="bg-white rounded-xl border border-stone-200 p-5 flex items-start gap-4">
            <input
              type="checkbox"
              checked={!!checks[item.id]}
              onChange={() => togglePrep(item.id)}
              disabled={isArchived}
              className="mt-1 h-5 w-5 rounded border-stone-300 text-cyan-600 cursor-pointer disabled:opacity-50"
            />
            <div className="flex-1">
              <p className="font-medium text-stone-800">{item.title}</p>
              <p className="text-stone-500 text-sm mt-1">{item.description}</p>
            </div>
          </div>
        ))}
      </div>

      {plannerPathUx ? (
        <section>
          <h2 className="text-lg font-semibold text-stone-900 mb-3">Automated Creditor Notifications</h2>
          {creditors.length === 0 ? (
            <p className="text-sm text-stone-600 mb-4">
              No creditor entries were imported from planning. Continue with manual creditor discovery below, or return
              after debts are documented in Frame 235.
            </p>
          ) : null}
          {creditors.length > 0 ? (
            <>
              <div className="hidden sm:grid grid-cols-[1.2fr,0.75fr,1fr,0.6fr_auto] gap-2 text-xs font-medium text-stone-500 uppercase tracking-wide pb-2 border-b border-stone-200 px-3">
                <span>Creditor Name</span>
                <span>Balance</span>
                <span>Contact / Method</span>
                <span>Documents</span>
                <span className="text-right">Actions</span>
              </div>
              <div className="space-y-2">
                {creditors.map((row) =>
                  editingId === row.id && draft ? (
                    <div
                      key={row.id}
                      className="rounded-xl border border-cyan-200 bg-cyan-50/40 p-4 grid gap-3 sm:grid-cols-2"
                    >
                      <label className="text-sm">
                        <span className="text-stone-500 block mb-1">Creditor Name</span>
                        <input
                          className="w-full rounded border border-stone-300 px-2 py-1.5 text-sm"
                          value={draft.creditorName ?? ''}
                          onChange={(e) => setDraft((d) => ({ ...(d ?? {}), creditorName: e.target.value }))}
                        />
                      </label>
                      <label className="text-sm">
                        <span className="text-stone-500 block mb-1">Balance</span>
                        <input
                          className="w-full rounded border border-stone-300 px-2 py-1.5 text-sm"
                          value={draft.balance ?? ''}
                          onChange={(e) => setDraft((d) => ({ ...(d ?? {}), balance: e.target.value }))}
                        />
                      </label>
                      <label className="text-sm sm:col-span-2">
                        <span className="text-stone-500 block mb-1">Contact Method</span>
                        <input
                          className="w-full rounded border border-stone-300 px-2 py-1.5 text-sm"
                          value={draft.contactMethod ?? ''}
                          onChange={(e) => setDraft((d) => ({ ...(d ?? {}), contactMethod: e.target.value }))}
                        />
                      </label>
                      <label className="text-sm sm:col-span-2">
                        <span className="text-stone-500 block mb-1">Documents</span>
                        <input
                          className="w-full rounded border border-stone-300 px-2 py-1.5 text-sm"
                          value={draft.documents ?? ''}
                          onChange={(e) => setDraft((d) => ({ ...(d ?? {}), documents: e.target.value }))}
                        />
                      </label>
                      <div className="flex gap-2 sm:col-span-2">
                        <button
                          type="button"
                          onClick={() => saveEditRow()}
                          className="px-4 py-2 rounded-lg bg-cyan-700 text-white text-sm"
                        >
                          Save
                        </button>
                        <button
                          type="button"
                          onClick={() => {
                            setEditingId(null);
                            setDraft(null);
                          }}
                          className="px-4 py-2 rounded-lg border border-stone-300 text-sm text-stone-700"
                        >
                          Cancel
                        </button>
                      </div>
                    </div>
                  ) : (
                    <div
                      key={row.id}
                      className="bg-white rounded-xl border border-stone-200 p-4 grid sm:grid-cols-[1.2fr,0.75fr,1fr,0.6fr_auto] gap-x-3 gap-y-2 items-center text-sm"
                    >
                      <span className="font-medium text-stone-800">{row.creditorName}</span>
                      <span className="text-stone-600">{row.balance}</span>
                      <span className="text-stone-600 truncate" title={row.contactMethod}>
                        {row.contactMethod}
                      </span>
                      <span className="text-stone-500 text-xs">{row.documents}</span>
                      <span className="flex justify-end gap-1 shrink-0">
                        <button
                          type="button"
                          aria-label={`Edit ${row.creditorName}`}
                          disabled={isArchived}
                          onClick={() => startEdit(row)}
                          className="rounded p-1.5 text-stone-500 hover:bg-stone-50 disabled:opacity-40"
                        >
                          <Pencil className="h-4 w-4" />
                        </button>
                        <button
                          type="button"
                          aria-label={`Delete ${row.creditorName}`}
                          disabled={isArchived}
                          onClick={() => deleteRow(row.id)}
                          className="rounded p-1.5 text-red-600 hover:bg-red-50 disabled:opacity-40"
                        >
                          <Trash2 className="h-4 w-4" />
                        </button>
                      </span>
                    </div>
                  )
                )}
              </div>
            </>
          ) : null}
        </section>
      ) : (
        <section className="space-y-8">
          <h2 className="text-lg font-semibold text-stone-900">Creditor Discovery & Manual Notice</h2>

          <div>
            <h3 className="text-sm font-medium text-stone-800 mb-2">Mail Scanner</h3>
            <div
              onDragOver={(e) => {
                e.preventDefault();
              }}
              onDrop={(e) => {
                e.preventDefault();
                void onDropMail(e.dataTransfer.files);
              }}
              className={`rounded-xl border-2 border-dashed border-stone-300 bg-stone-50 px-6 py-10 text-center transition-colors hover:border-cyan-400`}
            >
              <p className="text-sm text-stone-700 mb-2">Drag and drop creditor mail or envelopes here.</p>
              <input
                type="file"
                multiple
                className="mx-auto cursor-pointer max-w-[220px]"
                disabled={isArchived}
                onChange={(e) => void onDropMail(e.target.files)}
              />
              {mailScanFiles.length ? (
                <ul className="mt-4 text-xs text-left text-stone-600 inline-block mx-auto space-y-1">
                  {mailScanFiles.map((f, i) => (
                    <li key={`${f.name}-${i}`}>{f.name}</li>
                  ))}
                </ul>
              ) : null}
            </div>
          </div>

          <div>
            <h3 className="text-sm font-medium text-stone-800 mb-2">Pull Credit Reports</h3>
            <div className="rounded-xl border border-stone-200 bg-white p-4">
              <p className="text-sm text-stone-600 mb-3">Upload credit reports from each bureau.</p>
              <input type="file" multiple disabled={isArchived} onChange={(e) => void onDropCreditReports(e.target.files)} />
              {creditReports.length ? (
                <ul className="mt-3 text-xs text-stone-600 space-y-1">
                  {creditReports.map((f, i) => (
                    <li key={`${f.name}-cr-${i}`}>{f.name}</li>
                  ))}
                </ul>
              ) : null}
            </div>
          </div>
        </section>
      )}
    </div>
  );
}
